import { useEffect, useState } from "react";
import { X, Send, CheckCircle2, XCircle, PauseCircle, Archive, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_META, SUBMISSION_TYPES, SUBMISSION_STATUS_META } from "@/i18n/translations";

const STATUS_FILTERS = ["pending", "info_requested", "in_review", "approved", "rejected", "suspended", "archived"];

export default function SubmissionsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/submissions", { params: filter ? { status: filter } : {} }).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const refreshActive = async (id) => { const { data } = await api.get(`/admin/submissions/${id}`); setActive(data); };

  return (
    <div>
      <h1 className="mb-1 font-serif text-3xl text-anthracite">Soumissions membres</h1>
      <p className="mb-8 text-sm text-anthracite/50">Examinez, qualifiez, vérifiez et publiez les soumissions. Rien n'est publié sans validation RSTI.</p>

      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full border px-4 py-1.5 text-xs ${!filter ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>Tout</button>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`sub-filter-${s}`} className={`rounded-full border px-4 py-1.5 text-xs ${filter === s ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>{SUBMISSION_STATUS_META[s].fr}</button>
        ))}
      </div>

      <div className="overflow-hidden border border-tertiary bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-tertiary bg-sand text-left text-xs uppercase tracking-wider text-anthracite/50">
            <tr><th className="px-5 py-3">Titre</th><th className="px-5 py-3">Membre</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Statut</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-anthracite/40" data-testid="submissions-empty">Aucune soumission.</td></tr>
            ) : items.map((s) => {
              const st = SUBMISSION_STATUS_META[s.status];
              return (
                <tr key={s.id} data-testid={`admin-sub-${s.id}`} onClick={() => setActive(s)} className="cursor-pointer border-b border-tertiary last:border-0 hover:bg-sand/50">
                  <td className="px-5 py-3 font-medium text-anthracite">{s.title}</td>
                  <td className="px-5 py-3 text-anthracite/60">{s.submitter_name}<br /><span className="text-xs text-anthracite/40">{s.submitter_email}</span></td>
                  <td className="px-5 py-3 text-anthracite/60">{SUBMISSION_TYPES.find((t) => t.value === s.submission_type)?.fr}</td>
                  <td className="px-5 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-xs ${st.cls}`}>{st.fr}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {active && <SubmissionModal sub={active} onClose={() => setActive(null)} onChanged={() => { load(); }} refreshActive={refreshActive} />}
    </div>
  );
}

function SubmissionModal({ sub, onClose, onChanged, refreshActive }) {
  const [msg, setMsg] = useState("");
  const [pubLevel, setPubLevel] = useState("unverified");
  const [busy, setBusy] = useState(false);

  const act = async (status) => {
    if ((status === "info_requested" || status === "rejected") && !msg.trim()) {
      return toast.error(status === "info_requested" ? "Précisez les informations demandées." : "Indiquez le motif de refus.");
    }
    setBusy(true);
    try {
      await api.post(`/admin/submissions/${sub.id}/status`, { status, message: msg || undefined });
      toast.success("Statut mis à jour");
      await refreshActive(sub.id); onChanged();
      setMsg("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const publish = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${sub.id}/publish`, { status, verification_level: pubLevel });
      toast.success(status === "published" ? "Publié — affinez la fiche bilingue dans l'éditeur." : "Fiche créée en brouillon.");
      await refreshActive(sub.id); onChanged();
      window.open(`/admin/listings/${data.listing_id}/edit`, "_blank");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => { if (!window.confirm("Supprimer définitivement ?")) return; await api.delete(`/admin/submissions/${sub.id}`); onChanged(); onClose(); };

  const st = SUBMISSION_STATUS_META[sub.status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-anthracite/50 px-4 py-6" data-testid="submission-modal">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col bg-white">
        <div className="shrink-0 px-6 pt-6 pb-4 flex items-start justify-between">
          <div>
            <h2 className="font-serif text-2xl text-anthracite">{sub.title}</h2>
            <p className="text-xs text-anthracite/50">{sub.submitter_name} · {sub.submitter_email}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6" data-testid="submission-modal-body">

          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-anthracite/60">
            <span className={`rounded-full border px-2.5 py-0.5 ${st.cls}`}>{st.fr}</span>
            <span>{SUBMISSION_TYPES.find((t) => t.value === sub.submission_type)?.fr}</span>
            <span>· {CATEGORY_META[sub.category]?.fr}</span>
            {sub.published_listing_id && <a href={`/listing/${sub.published_listing_id}`} target="_blank" rel="noreferrer" className="text-champagne underline">Fiche liée</a>}
          </div>

          <div className="space-y-2 border-y border-tertiary py-4 text-sm text-anthracite/80">
            {sub.summary && <p><span className="text-anthracite/40">Résumé : </span>{sub.summary}</p>}
            {sub.description && <p className="whitespace-pre-line"><span className="text-anthracite/40">Description : </span>{sub.description}</p>}
            <p className="text-xs text-anthracite/50">{[sub.sector, sub.country, sub.region].filter(Boolean).join(" · ")} {sub.tags?.length ? "· " + sub.tags.join(", ") : ""}</p>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-xs uppercase tracking-[0.12em] text-anthracite/50">Message au membre / note (demande d'info, motif de refus)</label>
            <textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="sub-message" className="w-full border border-tertiary px-3 py-2 text-sm outline-none focus:border-champagne" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => act("info_requested")} data-testid="sub-request-info" className="inline-flex items-center gap-1.5 border border-amber-300 px-3 py-2 text-xs text-amber-700 hover:bg-amber-50"><Send className="h-3.5 w-3.5" /> Demander infos</button>
            <button disabled={busy} onClick={() => act("approved")} data-testid="sub-approve" className="inline-flex items-center gap-1.5 border border-green-300 px-3 py-2 text-xs text-green-700 hover:bg-green-50"><CheckCircle2 className="h-3.5 w-3.5" /> Approuver</button>
            <button disabled={busy} onClick={() => act("rejected")} data-testid="sub-reject" className="inline-flex items-center gap-1.5 border border-red-300 px-3 py-2 text-xs text-red-600 hover:bg-red-50"><XCircle className="h-3.5 w-3.5" /> Refuser</button>
            <button disabled={busy} onClick={() => act("suspended")} className="inline-flex items-center gap-1.5 border border-orange-300 px-3 py-2 text-xs text-orange-700 hover:bg-orange-50"><PauseCircle className="h-3.5 w-3.5" /> Suspendre</button>
            <button disabled={busy} onClick={() => act("archived")} className="inline-flex items-center gap-1.5 border border-tertiary px-3 py-2 text-xs text-anthracite/60 hover:bg-sand"><Archive className="h-3.5 w-3.5" /> Archiver</button>
            <button disabled={busy} onClick={del} className="inline-flex items-center gap-1.5 border border-tertiary px-3 py-2 text-xs text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Supprimer</button>
          </div>

        </div>
        <div className="shrink-0 border-t border-tertiary px-6 py-4 border-t border-tertiary pt-4">
          <p className="mb-2 text-xs uppercase tracking-[0.12em] text-anthracite/50">Publication (crée une fiche)</p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={pubLevel} onChange={(e) => setPubLevel(e.target.value)} className="border border-tertiary px-2 py-2 text-xs outline-none focus:border-champagne">
              <option value="unverified">Non vérifié</option>
              <option value="declared">Déclaré</option>
              <option value="verified">Vérifié</option>
              <option value="premium">Premium vérifié</option>
            </select>
            <button disabled={busy} onClick={() => publish("draft")} className="inline-flex items-center gap-1.5 border border-anthracite px-3 py-2 text-xs text-anthracite hover:bg-anthracite hover:text-ivory">Créer en brouillon</button>
            <button disabled={busy} onClick={() => publish("published")} data-testid="sub-publish" className="inline-flex items-center gap-1.5 bg-anthracite px-3 py-2 text-xs font-medium text-ivory hover:bg-black"><Upload className="h-3.5 w-3.5" /> Publier</button>
          </div>
        </div>
      </div>
    </div>
  );
}
